import { Injectable, Optional, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Initiative, InitiativeData, UpdateInitiativeData } from '../models/initiative.model';
import { PagedOverview } from 'src/app/core/models';

@Injectable({
  providedIn: 'root',
})
export class InitiativesService {
  constructor(
    private http: HttpClient,
    @Optional()
    @Inject('BASE_URL')
    private BASE_URL: string
  ) {}

  getInitiatives(skip: number = 0, take: number = 0, theme: number = 0, sortOrder: number = 0, lat = '', lng = '') {
    return this.http.get<PagedOverview<Initiative>>(
      `${this.BASE_URL}api/initiatives?take=${take}&skip=${skip}&theme=${theme}&sortOrder=${sortOrder}&lat=${lat}&lng=${lng}`
    );
  }

  getMyInitiatives() {
    return this.http.get<Initiative[]>(`${this.BASE_URL}api/initiatives/me`);
  }

  getInitiative(id: number) {
    return this.http.get<Initiative>(`${this.BASE_URL}api/initiatives/${id}`);
  }

  getRelatedInitiatives(id: number) {
    return this.http.get<{ score: number; source: Initiative }[]>(`${this.BASE_URL}api/initiatives/${id}/related-initiatives`);
  }

  createInitiative(data: InitiativeData) {
    const formData: FormData = new FormData();
    formData.append('title', data.title);
    formData.append('subtitle', data.subtitle);
    formData.append('intro', data.intro);
    formData.append('text', data.text);
    formData.append('entireArea', data.entireArea.toString());

    if (data.image instanceof Blob) {
      formData.append('image', data.image);
    } else {
      formData.append('image', <Blob>(null as unknown));
    }

    formData.append('imageAltText', data.imageAltText || '');
    formData.append('imageCaption', data.imageCaption || '');

    if (data.contactImage instanceof Blob) {
      formData.append('contactImage', data.contactImage);
    } else {
      formData.append('contactImage', <Blob>(null as unknown));
    }

    if (data.contactLogo instanceof Blob) {
      formData.append('contactLogo', data.contactLogo);
    } else {
      formData.append('contactLogo', <Blob>(null as unknown));
    }

    formData.append('contactImageAltText', data.contactImageAltText || '');
    formData.append('contactName', data.contactName || '');
    formData.append('contactEmail', data.contactEmail || '');
    formData.append('contactPhone', data.contactPhone || '');
    formData.append('showContactOnPlatform', data.showContactOnPlatform.toString());

    data.locations.forEach((location, i) => {
      formData.append(`locations[${i}].id`, location.id.toString());
      formData.append(`locations[${i}].label`, location.label);
      formData.append(`locations[${i}].lat`, location.lat.toString());
      formData.append(`locations[${i}].lng`, location.lng.toString());
    });

    data.themes.forEach((theme, i) => {
      formData.append(`themes[${i}].id`, theme.id.toString());
      formData.append(`themes[${i}].title`, theme.title);
      formData.append(`themes[${i}].icon`, theme.icon);
    });
    return this.http.post<Initiative>(`${this.BASE_URL}api/initiatives`, formData);
  }

  updateInitiative(id: number, data: UpdateInitiativeData) {
    const formData: FormData = this._buildInitiativeFormData(data);
    return this.http.put<Initiative>(`${this.BASE_URL}api/initiatives/${id}`, formData);
  }

  deleteInitiative(id: number) {
    return this.http.delete<Initiative>(`${this.BASE_URL}api/initiatives/${id}`);
  }

  private _buildInitiativeFormData(data: UpdateInitiativeData) {
    const formData: FormData = new FormData();
    formData.append('title', data.title);
    formData.append('subtitle', data.subtitle);
    formData.append('intro', data.intro);
    formData.append('text', data.text);
    formData.append('entireArea', data.entireArea.toString());

    if (data.image instanceof Blob) {
      formData.append('image', data.image);
    } else {
      formData.append('image', <Blob>(null as unknown));
    }

    formData.append('imageAltText', data.imageAltText || '');
    formData.append('imageCaption', data.imageCaption || '');

    if (data.contactImage instanceof Blob) {
      formData.append('contactImage', data.contactImage);
    } else {
      formData.append('contactImage', <Blob>(null as unknown));
    }

    if (data.contactLogo instanceof Blob) {
      formData.append('contactLogo', data.contactLogo);
    } else {
      formData.append('contactLogo', <Blob>(null as unknown));
    }

    formData.append('contactImageAltText', data.contactImageAltText || '');
    formData.append('contactName', data.contactName || '');
    formData.append('contactEmail', data.contactEmail || '');
    formData.append('contactPhone', data.contactPhone || '');
    formData.append('showContactOnPlatform', data.showContactOnPlatform.toString());

    data.themes.forEach((theme, i) => {
      formData.append(`themes[${i}].id`, theme.id.toString());
      formData.append(`themes[${i}].title`, theme.title);
      formData.append(`themes[${i}].icon`, theme.icon);
    });

    data.locations.forEach((location, i) => {
      formData.append(`locations[${i}].id`, location.id.toString());
      formData.append(`locations[${i}].label`, location.label);
      formData.append(`locations[${i}].lat`, location.lat.toString());
      formData.append(`locations[${i}].lng`, location.lng.toString());
    });

    formData.append('id', data.id?.toString());

    return formData;
  }

  likeInitiative(id: number) {
    return this.http.post<Initiative>(`${this.BASE_URL}api/content/${id}/vote`, null);
  }
}
